import { toCanvas } from 'html-to-image'
import jsPDF from 'jspdf'
import { flushSync } from 'react-dom'
import { createRoot, Root } from 'react-dom/client'

import { BVADetails } from 'model/BVADetails'
import { createPortalContainer } from 'utils/createPortalContainer'
import { autofitText } from 'utils/pdf/autofitText'
import { nextFrame, waitForImages } from 'utils/pdf/domReady'
import { getPdfFontEmbedCss, waitForPdfFonts } from 'utils/pdf/fontEmbed'
import { resolvePdfImages } from 'utils/pdf/imageDataUrl'

import { BvaPdfDocument } from './BvaPdfDocument'
import { BVA_PDF_HEIGHT, BVA_PDF_WIDTH } from './bvaPdfLayout'

const CAPTURE_PIXEL_RATIO = 2
/** JPEG: a full-bleed photographic PNG at this resolution runs 8-15MB for no visible gain. */
const CAPTURE_MIME: 'image/jpeg' = 'image/jpeg'
const CAPTURE_QUALITY = 0.95

const ILLEGAL_FILENAME_CHARS = /[\\/:*?"<>|]+/g

export const buildBvaPdfFilename = (details: BVADetails): string => {
  const stem = [details.brandName, details.year].filter(Boolean).join(' ')
  const cleaned = stem.replace(ILLEGAL_FILENAME_CHARS, '').trim()
  return `${cleaned || 'BVA'}.pdf`
}

const createOffscreenHost = (): HTMLDivElement => {
  const host = createPortalContainer()
  host.setAttribute('aria-hidden', 'true')
  host.dataset.bvaPdfHost = 'true'
  // Offscreen but fully laid out -- NOT display:none / visibility:hidden /
  // opacity:0. html-to-image clones computed styles onto its capture target,
  // so a hidden node rasterizes to nothing, and display:none has no layout at
  // all (every scrollHeight/clientHeight would read 0 and the fit pass would
  // no-op). The offset lives on the host; the statically-positioned stage
  // inside it is what gets captured.
  host.style.cssText = [
    'position: fixed',
    'top: 0',
    'left: -30000px',
    `width: ${BVA_PDF_WIDTH}px`,
    `height: ${BVA_PDF_HEIGHT}px`,
    'overflow: hidden',
    'pointer-events: none',
  ].join(';')
  return host
}

/**
 * Renders the offscreen BVA one-pager, rasterizes it, and downloads a
 * single-page 16:9 PDF. Pass an `AbortSignal` to cancel a still-running
 * export (e.g. the user navigated away from the brand detail page).
 */
export const exportBvaPdf = async (details: BVADetails, signal?: AbortSignal): Promise<void> => {
  const abortIfCancelled = () => {
    if (signal?.aborted) throw new DOMException('PDF export cancelled', 'AbortError')
  }

  // Fonts are cached for the whole session; images are resolved per BVA.
  const [fontEmbedCSS, images] = await Promise.all([getPdfFontEmbedCss(), resolvePdfImages(details)])
  abortIfCancelled()

  const host = createOffscreenHost()
  // Appended to document.body, NOT inside #root: `#root * { font-family:
  // 'TCCC-UnityText' }` in styles.scss would otherwise beat every
  // class-level font-family, and the template needs UnityCondensed and
  // UnityHeadline. Same trick as components/Toast/Toast.tsx.
  document.body.appendChild(host)

  let root: Root | null = null

  try {
    root = createRoot(host)
    const activeRoot = root
    // flushSync forces a synchronous commit, so the DOM is measurable the
    // moment this call returns -- no useLayoutEffect (can't await the font
    // load) and no StrictMode double-invoke, since this root lives outside
    // the app tree.
    flushSync(() => {
      activeRoot.render(
        <BvaPdfDocument
          brandName={details.brandName}
          year={details.year}
          logoUrl={images.logo}
          backgroundUrl={images.background ?? undefined}
          sections={details.sections}
        />,
      )
    })

    const stage = host.firstElementChild as HTMLElement | null
    if (!stage) throw new Error('BVA PDF template failed to mount')

    await waitForPdfFonts(stage.textContent ?? '')
    await waitForImages(stage)
    abortIfCancelled()

    autofitText(stage)
    // After the fit pass, not before: two frames give font-display and
    // layout time to settle, and this is also Safari's foreignObject font
    // cache workaround (see the double-capture below).
    await nextFrame()
    await nextFrame()
    abortIfCancelled()

    const captureOptions = {
      width: BVA_PDF_WIDTH,
      height: BVA_PDF_HEIGHT,
      pixelRatio: CAPTURE_PIXEL_RATIO,
      fontEmbedCSS,
      // Never set cacheBust: it appends ?t=<ts> to image URLs, which would
      // invalidate presigned blob signatures. Moot here since every image is
      // already a data URI, but worth never touching.
      cacheBust: false,
      // Mandatory with JPEG output: transparent pixels decode to black without it.
      backgroundColor: '#FFFFFF',
      style: { position: 'static', top: '0', left: '0', margin: '0', transform: 'none' },
    }

    // Safari only populates the foreignObject font cache from the second
    // capture pass; the throwaway first call is the standard workaround.
    await toCanvas(stage, captureOptions)
    const canvas = await toCanvas(stage, captureOptions)
    abortIfCancelled()

    const imageData = canvas.toDataURL(CAPTURE_MIME, CAPTURE_QUALITY)

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'px',
      format: [BVA_PDF_WIDTH, BVA_PDF_HEIGHT],
      compress: true,
    })
    doc.addImage(imageData, 'JPEG', 0, 0, BVA_PDF_WIDTH, BVA_PDF_HEIGHT)
    doc.save(buildBvaPdfFilename(details))
  } finally {
    const rootToUnmount = root
    // Deferred: unmounting synchronously here can race React's own commit
    // (root.unmount while a render from this same tick is still settling)
    // and gives the browser a moment to finish handing the saved blob to the
    // download sink before the DOM disappears.
    window.setTimeout(() => {
      rootToUnmount?.unmount()
      host.remove()
    }, 0)
  }
}
